// Case-wide financial computation layer (Round 12, Part 1) for the Financial
// summary. This DELIBERATELY revisits financialFlow.ts's "no cross-record
// totals" rule — but conservatively (the "verify-or-suppress" decision):
//
//   - Every number is integer cents via the SHARED helpers (toCents /
//     findExactFold). No floats, no widget-local arithmetic.
//   - A cross-record NET is the classic trust hazard: an amount whose flow
//     label is wrong (a settlement OFFER or a coverage CAP summed as a COST)
//     produces a wrong net. So the net/subtotals are ASSERTED only when every
//     contributing amount's category is USER-CONFIRMED (an explicit override
//     exists). Until then `totals` is null — the UI lists the per-record
//     figures + a "confirm categories" notice instead of a possibly-wrong
//     number. Confirming a category is the unlock.
//   - Category is an AI label (amount_flow / fact_category) and may be wrong:
//     it is shown, is user-overridable (content-keyed, survives re-extraction),
//     and NEVER hides a number — every usable amount appears in `items`, every
//     non-usable one in `excluded`.
//
// Net = Σcost − Σrecovery. coverage_term and rate are reference subtotals
// (caps, deductibles-as-terms, per-unit rates) and are excluded from the net.
//
// Pure functions, no React, no AI — unit-tested via node --test.

import type { RecordFact } from "../types";
import { parseAmount } from "./factMeta";
import { toCents } from "./costsMath";
import { findExactFold, flowOf } from "./financialFlow";
import { expenseFactKey } from "./expenseStatement";

export type LedgerBucket = "cost" | "recovery" | "coverage_term" | "rate";

// A disposition is the user's per-amount decision: one of the four type
// buckets, OR "exclude" — "this amount is the SAME money already counted
// elsewhere, leave it out of the total". Exclude exists for overlaps the
// per-record fold can't catch: the SAME figure restated in a second record
// (a forwarded estimate), or a gross/net pair split across records. It is a
// disposition, NOT a fifth type — the type dropdown stays four-way.
export type LedgerDisposition = LedgerBucket | "exclude";

export const BUCKET_LABELS: Record<LedgerBucket, string> = {
  cost: "Cost incurred",
  recovery: "Recovery / received",
  coverage_term: "Coverage term",
  rate: "Rate",
};

// Stable content key for a category override — the SAME key the expense
// statement uses, so the two override stores stay consistent and an override
// survives re-extraction.
export function categoryFactKey(f: RecordFact): string {
  return expenseFactKey(f);
}

// Default bucket from the AI labels. fact_category 'coverage_term' wins (a
// deductible/cap is a policy term, not a cash movement); otherwise amount_flow
// drives it. 'offer' is a prospective recovery → recovery bucket. NULL/unknown
// flow falls to 'cost' (legacy out-of-pocket rows), matching flowOf.
export function defaultBucket(f: RecordFact): LedgerBucket {
  if ((f.fact_category ?? "case_event") === "coverage_term") return "coverage_term";
  switch (flowOf(f)) {
    case "rate":
      return "rate";
    case "recovery":
    case "offer":
      return "recovery";
    default:
      return "cost";
  }
}

// The user-facing disposition for a fact: explicit override if present, else
// the AI default bucket. An override (ANY value, including "exclude") marks
// the fact "confirmed".
export type CategoryOverrides = ReadonlyMap<string, LedgerDisposition>;

// The TYPE bucket for a fact, ignoring exclude. "exclude" is a participation
// decision, not a type — the dropdown shows the underlying best-guess type so
// the user still sees what it is. Explicit bucket override wins; an "exclude"
// override falls back to the AI default type.
export function bucketOf(f: RecordFact, overrides: CategoryOverrides): LedgerBucket {
  const ov = overrides.get(categoryFactKey(f));
  return ov && ov !== "exclude" ? ov : defaultBucket(f);
}

export function isExcluded(f: RecordFact, overrides: CategoryOverrides): boolean {
  return overrides.get(categoryFactKey(f)) === "exclude";
}

export interface LedgerItem {
  fact: RecordFact;
  key: string;
  cents: number;
  bucket: LedgerBucket;
  // True when the user has explicitly set a disposition (override present, any
  // value). Drives the verify-or-suppress gate.
  confirmed: boolean;
  // User marked this amount "exclude" — the SAME money already counted. It
  // contributes to no bucket and never blocks the gate.
  excluded: boolean;
  // Auto-subsumed: this cash amount is an exact component of a same-record
  // cash TOTAL (e.g. the net that folds under the gross). It is represented by
  // that total, so it contributes nothing to the net and never blocks the
  // gate. subsumedByCents is the total it rolls into (for the "part of $X"
  // label). This is the per-record double-count guard.
  subsumed: boolean;
  subsumedByCents: number | null;
}

export interface ExcludedAmount {
  fact: RecordFact;
  // Why it can't enter the math: no verified value, or an unparseable amount.
  reason: "unverified" | "no_parseable_value";
}

// THE single per-bucket subtotal formula. A bucket's subtotal is the exact
// integer-cent sum of its PARTICIPATING items — verified, not user-excluded,
// not fold-subsumed. Every financial surface calls this; no one re-implements
// the sum, so subtotals can never diverge between the net, the prompt ledger,
// and the costs-by-context table.
// THE one integer-cent summation. Everything that totals money — the case
// net, bucket subtotals, and the reimbursement ledger's section subtotals —
// routes through this, so no two surfaces can sum differently.
export function sumCents(centsList: number[]): number {
  return centsList.reduce((s, n) => s + n, 0);
}

export function bucketSubtotalCents(
  items: LedgerItem[],
  bucket: LedgerBucket,
): number {
  return sumCents(
    items
      .filter((i) => i.bucket === bucket && !i.excluded && !i.subsumed)
      .map((i) => i.cents),
  );
}

export interface AssertedTotals {
  costCents: number;
  recoveryCents: number;
  netCents: number;
  // Per-bucket exact subtotals (every bucket that has at least one item).
  bucketSubtotals: Array<{ bucket: LedgerBucket; cents: number }>;
}

export interface CaseTotals {
  // Every usable verified amount, with its (overridable) bucket.
  items: LedgerItem[];
  // Amounts that cannot enter the computation, surfaced separately so a
  // number is never silently dropped.
  excluded: ExcludedAmount[];
  // True iff the cross-record net/subtotals are safe to assert (every
  // cost/recovery item is user-confirmed).
  asserted: boolean;
  // When not asserted: why (for the UI notice). Null when asserted.
  reason: string | null;
  // Present ONLY when asserted — never render a total when this is null.
  totals: AssertedTotals | null;
  // Amounts still awaiting a user decision (not confirmed, not subsumed by a
  // fold total) — the confirm worklist's contents and the "N to confirm"
  // count. Subsumed components are resolved by their parent and never listed.
  toConfirm: LedgerItem[];
}

// A verified amount fact is "usable" when verified and it carries a parseable
// non-zero value. We collapse exact same-record duplicates (identical content
// key) so a figure quoted twice in one record is counted once — never across
// records, which would be unsafe.
export function computeCaseTotals(
  facts: RecordFact[],
  overrides: CategoryOverrides,
): CaseTotals {
  const amountFacts = facts.filter((f) => f.fact_type === "amount");

  const excluded: ExcludedAmount[] = [];
  const byKey = new Map<string, LedgerItem>();

  for (const f of amountFacts) {
    if (!f.verified) {
      excluded.push({ fact: f, reason: "unverified" });
      continue;
    }
    const cents = toCents(parseAmount(f.amount));
    if (cents === 0) {
      excluded.push({ fact: f, reason: "no_parseable_value" });
      continue;
    }
    const key = categoryFactKey(f);
    if (byKey.has(key)) continue; // exact same-record duplicate — count once
    byKey.set(key, {
      fact: f,
      key,
      cents,
      bucket: bucketOf(f, overrides),
      confirmed: overrides.has(key),
      excluded: isExcluded(f, overrides),
      subsumed: false,
      subsumedByCents: null,
    });
  }

  const items = [...byKey.values()];

  // Per-record double-count guard: when a record's amounts contain an exact
  // fold (components sum exactly to a stated total), the components are the
  // SAME money as the total. If the TOTAL is a cash item (cost/recovery) that
  // stays in the net, its cash components are double-counts — subsume them.
  // We keep exactly one representation: the total. This reuses the SAME shared
  // exact fold that drives the "✓ reconciles" badge, so the math the user sees
  // and the math we total are one and the same.
  markSubsumedComponents(items);

  // A cash amount blocks the gate only if it actually enters the net: not
  // excluded by the user, not subsumed by a fold total.
  const cashItems = items.filter(
    (i) =>
      (i.bucket === "cost" || i.bucket === "recovery") &&
      !i.excluded &&
      !i.subsumed,
  );
  const unconfirmed = cashItems.filter((i) => !i.confirmed).length;
  const asserted = items.length > 0 && unconfirmed === 0;

  // The worklist: anything still awaiting a decision. Subsumed components are
  // resolved by their parent total, so they're never asked about.
  const toConfirm = items.filter((i) => !i.confirmed && !i.subsumed);

  if (!asserted) {
    return {
      items,
      excluded,
      asserted: false,
      reason:
        items.length === 0
          ? "No verified amounts to total."
          : `${unconfirmed} amount${unconfirmed === 1 ? "" : "s"} ${
              unconfirmed === 1 ? "has" : "have"
            } an unconfirmed category — confirm categories to compute case totals.`,
      totals: null,
      toConfirm,
    };
  }

  // Sum only amounts that participate: not excluded, not subsumed. THE ONE
  // subtotal formula — every surface (asserted net, ledgerSummary, the
  // costs-by-context table) routes through bucketSubtotalCents so the numbers
  // can never drift.
  const costCents = bucketSubtotalCents(items, "cost");
  const recoveryCents = bucketSubtotalCents(items, "recovery");

  const buckets: LedgerBucket[] = ["cost", "recovery", "coverage_term", "rate"];
  const bucketSubtotals = buckets
    .map((bucket) => ({ bucket, cents: bucketSubtotalCents(items, bucket) }))
    .filter((b) =>
      items.some(
        (i) => i.bucket === b.bucket && !i.excluded && !i.subsumed,
      ),
    );

  return {
    items,
    excluded,
    asserted: true,
    reason: null,
    totals: {
      costCents,
      recoveryCents,
      netCents: costCents - recoveryCents,
      bucketSubtotals,
    },
    toConfirm,
  };
}

// Mutates `items` in place: for each record, find the exact fold over its
// amounts. When the fold's stated TOTAL is a counted cash item (cost/recovery)
// — the kind that stays in the net — mark each CASH component as subsumed (it
// is the same money as the total). Coverage-term / rate components are already
// out of the net, so they're left visible as their own reference subtotal. A
// fold whose total is itself coverage_term/rate is NOT used to subsume cash —
// that would wrongly drop real costs that merely happen to sum to a cap.
//
// PRECEDENCE: the fold is an automatic GUESS, and a user decision always wins.
// A component the user has already touched (any override — "count anyway" or
// "exclude") is left exactly as the user set it; the fold never overrides it.
// Likewise a user-excluded total counts for nothing, so it can't subsume its
// components. Every subsumption is recorded on the item (subsumed +
// subsumedByCents) so the worklist can SHOW it with a reason and an undo —
// nothing leaves the net silently.
function markSubsumedComponents(items: LedgerItem[]): void {
  const byRecord = new Map<string, LedgerItem[]>();
  for (const it of items) {
    const g = byRecord.get(it.fact.record_id);
    if (g) g.push(it);
    else byRecord.set(it.fact.record_id, [it]);
  }
  for (const group of byRecord.values()) {
    if (group.length < 3) continue;
    const fold = findExactFold(group.map((i) => i.cents));
    if (!fold) continue;
    const total = group[fold.totalIndex]!;
    if (total.bucket !== "cost" && total.bucket !== "recovery") continue;
    if (total.excluded) continue; // an excluded total represents nothing
    for (const ci of fold.componentIndices) {
      const comp = group[ci]!;
      if (comp.bucket !== "cost" && comp.bucket !== "recovery") continue;
      if (comp.confirmed) continue; // user decision wins over the fold guess
      comp.subsumed = true;
      comp.subsumedByCents = total.cents;
    }
  }
}

// ---------------------------------------------------------------------------
// Overage: actual vs. a verified limit/approved figure (Part 1). Pure integer
// cents. Handles zero and negative gaps (under the limit) symmetrically.
// ---------------------------------------------------------------------------

export interface Overage {
  actualCents: number;
  limitCents: number;
  // actual − limit. Positive = over the limit by this much; negative = under.
  gapCents: number;
  over: boolean;
  // Magnitude under the limit (0 when over or exactly at the limit).
  underByCents: number;
}

export function computeOverage(actualCents: number, limitCents: number): Overage {
  const gapCents = actualCents - limitCents;
  return {
    actualCents,
    limitCents,
    gapCents,
    over: gapCents > 0,
    underByCents: gapCents < 0 ? -gapCents : 0,
  };
}

// ---------------------------------------------------------------------------
// Reconciliation (Part 1): assert that components + stated total agree, using
// the SHARED exact fold. `reconcile` returns the fold when a stated total is
// exactly explained by a subset of its siblings (e.g. gross −2,000 deductible
// = 3,751.80 net). When a set CLAIMS a total (total/sum/overall/grand) but no
// exact fold exists, the arithmetic is contradicted — flag it, don't hide it.
// ---------------------------------------------------------------------------

const TOTAL_LANGUAGE_RE = /\b(?:total|sum|overall|grand|net)\b/i;

export interface Reconciliation {
  reconciles: boolean;
  totalIndex: number | null;
  componentIndices: number[];
  // True when some line calls itself a total/net but the set does NOT fold —
  // a stated total that disagrees with its components.
  discrepancy: boolean;
}

// Records whose verified amounts include a line that calls itself a total/net
// but whose siblings do NOT fold to it — a stated total contradicting its
// components. Shared by the Financial summary (collapsed actionable count) and
// the per-record discrepancy badge so both agree. A record needs ≥3 amount
// facts before a fold is meaningful.
export function discrepancyRecordIds(facts: RecordFact[]): Set<string> {
  const flagged = new Set<string>();
  const byRecord = new Map<string, RecordFact[]>();
  for (const f of facts) {
    if (f.fact_type !== "amount" || !f.verified) continue;
    if (parseAmount(f.amount) === 0) continue;
    const g = byRecord.get(f.record_id);
    if (g) g.push(f);
    else byRecord.set(f.record_id, [f]);
  }
  for (const [id, group] of byRecord) {
    if (group.length < 3) continue;
    const r = reconcileSignedCents(
      group.map((f) => toCents(parseAmount(f.amount))),
      group.map((f) => f.summary ?? ""),
    );
    if (r.discrepancy) flagged.add(id);
  }
  return flagged;
}

export function reconcileSignedCents(
  cents: number[],
  descriptions: string[],
): Reconciliation {
  const fold = findExactFold(cents);
  const componentSet = new Set(fold?.componentIndices ?? []);
  const discrepancy = descriptions.some(
    (d, i) =>
      i !== (fold?.totalIndex ?? -1) &&
      !componentSet.has(i) &&
      TOTAL_LANGUAGE_RE.test(d ?? ""),
  );
  return {
    reconciles: fold !== null,
    totalIndex: fold?.totalIndex ?? null,
    componentIndices: fold?.componentIndices ?? [],
    discrepancy,
  };
}
